export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .substring(0, 100); // Limit length
}

// Extract short ID from UUID (first 8 chars are unique enough for URLs)
function getShortId(uuid: string): string {
  return uuid.split('-')[0]; // Returns first 8 characters
}

// Check if string looks like a short ID (8 hex chars)
function isShortId(str: string): boolean {
  return /^[0-9a-f]{8}$/i.test(str);
}

// Check if string is a full UUID
function isFullUuid(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

export function parseVideoSlug(slug: string): { id: string | null; titleSlug: string | null } {
  // Format with ID: "video-title-slug--shortid" (e.g., "sherman-irby--a2222222")
  const parts = slug.split('--');
  if (parts.length === 2) {
    const idPart = parts[1];
    // Handle both short ID and full UUID formats
    if (isShortId(idPart) || isFullUuid(idPart)) {
      return { titleSlug: parts[0], id: idPart };
    }
  }
  // Fallback: treat entire slug as ID (for old UUID-only links)
  if (isFullUuid(slug)) {
    return { id: slug, titleSlug: null };
  }
  // Could be a short ID directly
  if (isShortId(slug)) {
    return { id: slug, titleSlug: null };
  }
  // Clean slug only (no ID) - new format
  return { id: null, titleSlug: slug };
}

export function createVideoUrl(id: string, title: string, hasDuplicate = false): string {
  const slug = generateSlug(title);
  if (hasDuplicate) {
    const shortId = getShortId(id);
    return `/video/${slug}--${shortId}`;
  }
  return `/video/${slug}`;
}

export function createSeriesUrl(id: string, title: string, hasDuplicate = false): string {
  const slug = generateSlug(title);
  if (hasDuplicate) {
    const shortId = getShortId(id);
    return `/series/${slug}--${shortId}`;
  }
  return `/series/${slug}`;
}

export function parseSeriesSlug(slug: string): { id: string | null; titleSlug: string | null } {
  return parseVideoSlug(slug); // Same logic
}

export function createArtistUrl(id: string, name: string, hasDuplicate = false): string {
  const slug = generateSlug(name);
  if (hasDuplicate) {
    const shortId = getShortId(id);
    return `/artist/${slug}--${shortId}`;
  }
  return `/artist/${slug}`;
}

export function parseArtistSlug(slug: string): { id: string | null; titleSlug: string | null } {
  return parseVideoSlug(slug); // Same logic
}
